import { useEffect, useState } from "react";
import { z } from "zod";
import { BusinessService } from "../../services/business.service";
import { CustomICreateBusiness, IBusinessResponse } from "../../types/business";
import { UploadService } from "../../services/upload.service";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import showErrorToast, {
  ErrorToastConfig,
  showSuccessToast,
  SuccessToastConfig,
} from "../../components/Error";
import { IAddressResponse, IProfileResponse } from "../../types/profile";
import { ITax } from "../../types";
import { TaxService } from "../../services/tax.service";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { DefaultCurrencies } from "../../utils/constants";
import { ApplicableAdminFeeTypes } from "../../types/applicable-admin-fee.enum";
import { BusinessSessionHelper } from "../../helpers/business-session.helper";
import { KycPaymentGatewayService } from "../../services/kyc-payment-gateway.service";

const useContainer = () => {
  //#region local states
  const { businessId } = useParams();

  const navigate = useNavigate();

  const imgExistanceCheckQuery = useQuery({
    queryKey: ["business-profile-image", businessId],
    queryFn: () => UploadService.checkIfImageExists(businessId!),
    enabled: !!businessId,
    retry: false,
    staleTime: 0,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState<IProfileResponse | null>(null);
  const [displayPicture, setDisplayPicture] = useState<string | null>(null);
  const [address, setAddress] = useState<IAddressResponse>();
  const [accountBusiness, setAccountBusiness] =
    useState<CustomICreateBusiness>();
  const [companyIcon, setCompanyIcon] = useState<File | null>(null);
  const [uploadedProfilePicture, setUploadedProfilePicture] =
    useState<File | null>(null);
  const [uploadedCompanyPicture, setUploadedCompanyPicture] =
    useState<File | null>(null);
  const [selectedTaxes, setSelectedTaxes] = useState<string[]>([]);
  const [defaultTaxId, setDefaultTaxId] = useState<string>();
  const [companyProfileFile, setCompanyProfileFile] = useState<File | null>(
    null
  );
  const [personalIDFile, setPersonalIDFile] = useState<File | null>(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [withKyc, setWithKyc] = useState(false);

  const baseSchema = z
    .object({
      companyName: z.string().min(3),
      companyNumber: z.string().optional(),
      companyNumberCountryCode: z
        .string()
        .optional()
        .transform((arg, ctx) => {
          if (arg) {
            if (!new RegExp(/^[0-9]+$/).test(arg)) {
              ctx.addIssue({
                code: "custom",
                message: "Should be numbers",
              });
            }
          }
          return arg;
        }),
      companyWebsite: z.string().optional(),
      companyCountry: z.string(),
      companyProvince: z.string(),
      companyAddress: z.string(),
      companyAddress2: z.string().optional().nullable(),
      companyPostalCode: z
        .string()
        .optional()
        .transform((arg, ctx) => {
          if (arg) {
            if (!new RegExp(/^[0-9]+$/).test(arg)) {
              ctx.addIssue({
                code: "custom",
                message: "Should be numbers",
              });
            }
          }
          return arg;
        }),
      companyCurrency: z.string().optional(),
      companyBankName: z.string().optional().nullable(),
      companyBankAccountNo: z
        .string()
        .optional()
        .transform((arg, ctx) => {
          if (arg) {
            if (!new RegExp(/^[0-9]+$/).test(arg)) {
              ctx.addIssue({
                code: "custom",
                message: "Should be number",
              });
            }
          }
          return arg;
        }),
      companyBankAccountHolderName: z.string().optional(),
      companyPaymentTerms: z.array(z.string()).optional(),
      applicableAdminFee: z.string().optional(),
      // kyc fields
      estimatedAnualIncomeCurrency: z.string().default("IDR"),
      estimatedAnualIncome: z.string(),
      averageValuePerTransactionCurrency: z.string().default("IDR"),
      averageValuePerTransaction: z.string(),
      bankName: z.string().optional(),
      accountNumber: z.string(),
      companyProfileFileUrl: z.string().optional(),
      personalIdFileUrl: z.string().optional(),
    })
    .refine(
      (data) => {
        if (withKyc) {
          return (
            data.estimatedAnualIncome !== "" ||
            data.averageValuePerTransaction !== "" ||
            data.bankName !== "" ||
            data.accountNumber.length > 5
          );
        }

        return true;
      },
      {
        path: [
          "estimatedAnualIncome",
          "averageValuePerTransaction",
          "bankName",
          "accountNumber",
        ],
      }
    );

  const methods = useForm<z.infer<typeof baseSchema>>({
    resolver: zodResolver(baseSchema),
    mode: "onChange",
  });

  const { watch, reset, getValues } = methods;
  const companyCountry = watch("companyCountry");

  const defaultCurrency = DefaultCurrencies.find(
    (cur) => cur.country === accountBusiness?.companyCountry
  );

  //#endregion
  //#region ----- life cycle methods

  const updateMutation = useMutation({
    mutationFn: BusinessService.updateBusiness,
    onSuccess: async (data) => {
      taxAssociateMutation.mutate({
        businessId: data.id,
        taxIds: selectedTaxes,
      });
      await BusinessSessionHelper.SetActiveBusiness({
        id: data.id,
      } as IBusinessResponse)
        .then(() => {
          localStorage.removeItem("upgrade-form-cache");
          if (withKyc) {
            const formData = getValues();
            kycMutation.mutate({
              ...formData,
              companyProfileFileUrl: formData.companyProfileFileUrl ?? "",
              personalIdFileUrl: formData.personalIdFileUrl ?? "",
            } as any);
          } else {
            navigate("/client", { replace: true });
          }
        })
        .catch((err) => {
          toast.error(
            "Something went wrong while updating the local business session."
          );
          console.log(err);
        });
    },
    onError: (error: any) => {
      setIsLoading(false);
      toast(showErrorToast(error), ErrorToastConfig);
    },
  });

  const taxAssociateMutation = useMutation({
    mutationFn: TaxService.associateTaxesOption,
    onSuccess: () => {
      setIsLoading(false);
      //   setIsSuccess(true);
      setTimeout(() => {
        navigate("/client/profile", { replace: true });
      }, 2000);
    },
    onError: (error: any) => {
      setIsLoading(false);
      toast(showErrorToast(error), ErrorToastConfig);
    },
  });

  const taxesOptionsQuery = useQuery({
    queryKey: ["taxes", companyCountry],
    queryFn: () => TaxService.getTaxesOption(companyCountry ?? ""),
    enabled: !!companyCountry,
    retry: false,
    staleTime: 0,
  });

  const kycMutation = useMutation({
    mutationFn: KycPaymentGatewayService.submit,
    onSuccess: () => {
      navigate("/client", { replace: true });
    },
    onError: (error: any) => {
      toast(showErrorToast(error), ErrorToastConfig);
    },
  });

  useEffect(() => {
    let mounted = true;
    if (!businessId) {
      return;
    }

    const fetchImage = async () => {
      if (!imgExistanceCheckQuery.data || !accountBusiness?.id) {
        if (mounted) {
          setCompanyIcon(null);
        }
        return;
      }

      try {
        const file = await UploadService.urlToFile(
          imgExistanceCheckQuery.data,
          businessId + Date.now()
        );

        if (mounted) {
          setCompanyIcon(file);
        }
      } catch (error) {
        console.error("Failed to fetch image:", error);
        if (mounted) {
          setCompanyIcon(null);
        }
      }
    };

    fetchImage();

    return () => {
      mounted = false;
    };
  }, [imgExistanceCheckQuery.data, businessId]);

  useEffect(() => {
    setDefaultTaxId(undefined);
    setSelectedTaxes([]);
  }, [companyCountry]);

  /**
   * Fetches the business associated with the given business ID.
   * If one is found, updates the local state with it.
   * @param businessId The ID of the business to fetch.
   */
  async function getBusiness(businessId: string) {
    if (localStorage.getItem("upgrade-form-cache")) {
      const cachedData = JSON.parse(
        localStorage.getItem("upgrade-form-cache") ?? "{}"
      );
      setAccountBusiness(cachedData);

      reset({
        companyName: cachedData?.companyName || undefined,
        companyNumber: cachedData?.companyNumber || undefined,
        companyNumberCountryCode:
          cachedData?.companyNumberCountryCode || undefined,
        companyWebsite: cachedData?.companyWebsite || undefined,
        companyCountry: cachedData?.companyCountry || undefined,
        companyProvince: cachedData?.companyProvince || undefined,
        companyAddress: cachedData?.companyAddress || undefined,
        companyAddress2: cachedData?.companyAddress2 || undefined,
        companyPostalCode: cachedData?.companyPostalCode || undefined,
        companyCurrency:
          cachedData.companyCurrency ??
          DefaultCurrencies.find(
            (cur) => cur.country === cachedData?.companyCountry
          )?.id,
        applicableAdminFee:
          cachedData?.applicableAdminFee ||
          ApplicableAdminFeeTypes.CHARGE_TO_SELLER,
        companyBankName: cachedData.companyBankName,
        companyBankAccountNo: cachedData.companyBankAccountNo,
        companyBankAccountHolderName: cachedData.companyBankAccountHolderName,
        companyPaymentTerms: cachedData.companyPaymentTerms ?? [],
      });
    }

    const businessData = await BusinessService.getBusinessById(businessId);
    if (businessData) {
      setAccountBusiness({ ...businessData, step: 1 });
      console.log("BUSINESS DATA", businessData);

      reset({
        companyName: businessData?.companyName || undefined,
        companyNumber: businessData?.companyNumber || undefined,
        companyNumberCountryCode:
          businessData?.companyNumberCountryCode || undefined,
        companyWebsite: businessData?.companyWebsite || undefined,
        companyCountry: businessData?.companyCountry || undefined,
        companyProvince: businessData?.companyProvince || undefined,
        companyAddress: businessData?.companyAddress || undefined,
        companyAddress2: businessData?.companyAddress2 || undefined,
        companyPostalCode: businessData?.companyPostalCode || undefined,
        companyCurrency:
          businessData?.bankInfo[0].currency ??
          DefaultCurrencies.find(
            (cur) => cur.country === businessData?.companyCountry
          )?.id,
        applicableAdminFee:
          businessData?.applicableAdminFee ||
          ApplicableAdminFeeTypes.CHARGE_TO_SELLER,
        companyBankName: businessData?.bankInfo[0].bankName,
        companyBankAccountNo: businessData?.bankInfo[0].accountNo,
        companyBankAccountHolderName:
          businessData?.bankInfo[0].accountHolderName,
        companyPaymentTerms: businessData?.companyPaymentTerms ?? [],
      });
    }
  }

  //#endregion
  //#region ----- handler methods

  /**
   * Handles the upload of a new profile picture.
   * @param file The file to be uploaded.
   */
  const handleUploadAccountPicture = (
    file: File,
    id: string,
    folder?: string
  ) => {
    if (!profile) return;

    setIsLoading(true);
    UploadService.upload(file, id, folder)
      .then((res) => {
        console.log(res);
        toast(
          showSuccessToast("Picture uploaded successfully"),
          SuccessToastConfig
        );
      })
      .catch((err) => {
        toast(showErrorToast(err), ErrorToastConfig);
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Maps the address to the company address fields if the company address fields are not populated.
   * @param {object} business the business object
   * @param {object} address the address object
   * @returns {object} the merged object with company address fields
   */
  const handleMapAddressToCompanyAddress = (business?: any, address?: any) => {
    const fields = {
      companyCountry: ["companyCountry", "countryCode"],
      companyNumber: ["companyNumber", "phoneNumber"],
      companyNumberCountryCode: [
        "companyNumberCountryCode",
        "phoneNumberCountryCode",
      ],
      companyAddress: ["companyAddress", "address"],
      companyAddress2: ["companyAddress2", "address2"],
      companyProvince: ["companyProvince", "province"],
      companyPostalCode: ["companyPostalCode", "postalCode"],
    };

    const result: any = {};
    for (const [key, [businessField, addressField]] of Object.entries(fields)) {
      result[key] = business?.[businessField] || address?.[addressField];
    }

    return result;
  };

  //#endregion

  return {
    isLoading,
    setIsLoading,
    profile,
    setProfile,
    displayPicture,
    setDisplayPicture,
    address,
    setAddress,
    accountBusiness,
    setAccountBusiness,
    companyIcon,
    setCompanyIcon,
    uploadedProfilePicture,
    setUploadedProfilePicture,
    uploadedCompanyPicture,
    setUploadedCompanyPicture,
    selectedTaxes,
    setSelectedTaxes,
    defaultTaxId,
    setDefaultTaxId,
    companyProfileFile,
    personalIDFile,
    successModalOpen,
    setSuccessModalOpen,
    setCompanyProfileFile,
    setPersonalIDFile,
    withKyc,
    setWithKyc,
    methods,
    taxOptions: (taxesOptionsQuery.data || []) as ITax[],
    defaultCurrency,
    getBusiness,
    handleMapAddressToCompanyAddress,
    handleUploadAccountPicture,
    updateMutation,
    imgExistanceCheckQuery,
  };
};

export default useContainer;
